import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { SHARK_SPECIES } from '../../../components/SharkAvatarSelector';
import { useUpdateCopilotSettingsMutation, useGetMeQuery } from '@/features/admin/api/adminApi';

export function useSharkSettings() {
  const adminRaw = localStorage.getItem('admin_user');
  const adminUser = adminRaw ? JSON.parse(adminRaw) : null;

  const [shark, setShark] = useState({
    name: adminUser?.sharkName || adminUser?.shark_name || 'Finn',
    species: adminUser?.sharkAvatar || adminUser?.shark_avatar || 'blue_shark',
    costume: adminUser?.sharkCostume || adminUser?.shark_costume || 'default',
  });

  const [direction, setDirection] = useState(0);

  const { data: meData } = useGetMeQuery();

  useEffect(() => {
    if (meData?.data) {
      const freshUser = meData.data;
      const freshAvatar = freshUser.sharkAvatar || freshUser.shark_avatar || 'blue_shark';
      const freshName = freshUser.sharkName || freshUser.shark_name || 'Finn';
      const freshCostume = freshUser.sharkCostume || freshUser.shark_costume || 'default';
      
      setShark(prev => ({
        ...prev,
        species: freshAvatar,
        name: freshName,
        costume: freshCostume
      }));

      const raw = localStorage.getItem('admin_user');
      if (raw) {
        const obj = JSON.parse(raw);
        obj.sharkAvatar = freshAvatar;
        obj.sharkName = freshName;
        obj.sharkCostume = freshCostume;
        localStorage.setItem('admin_user', JSON.stringify(obj));
      }
    }
  }, [meData]);

  const currentIndex = SHARK_SPECIES.findIndex(s => s.id === shark.species);
  const currentShark = SHARK_SPECIES[currentIndex !== -1 ? currentIndex : 0];
  const currentCostumeObj = currentShark.costumes.find(c => c.id === shark.costume) || currentShark.costumes[0];

  const navigateShark = (dir: number) => {
    setDirection(dir);
    const nextIndex = (currentIndex + dir + SHARK_SPECIES.length) % SHARK_SPECIES.length;
    const nextShark = SHARK_SPECIES[nextIndex];
    setShark(prev => ({
      ...prev,
      species: nextShark.id,
      costume: nextShark.costumes.some(c => c.id === prev.costume) ? prev.costume : 'default'
    }));
  };

  const [updateCopilotSettings, { isLoading: isSaving }] = useUpdateCopilotSettingsMutation();

  const handleSave = async () => {
    try {
      const res = await updateCopilotSettings({
        sharkAvatar: shark.species,
        sharkName: shark.name || currentShark.defaultName,
        sharkCostume: shark.costume
      }).unwrap();

      const updatedAdmin = res.data;

      const raw = localStorage.getItem('admin_user');
      if (raw) {
        const adminObj = JSON.parse(raw);
        adminObj.sharkAvatar = updatedAdmin.sharkAvatar || updatedAdmin.shark_avatar || shark.species;
        adminObj.sharkName = updatedAdmin.sharkName || updatedAdmin.shark_name || shark.name || currentShark.defaultName;
        adminObj.sharkCostume = updatedAdmin.sharkCostume || updatedAdmin.shark_costume || shark.costume;
        localStorage.setItem('admin_user', JSON.stringify(adminObj));
      }

      Swal.fire({
        title: 'Settings Saved',
        text: 'Your Shark settings have been successfully updated.',
        icon: 'success',
        confirmButtonColor: '#38bdf8',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)'
      });
    } catch (error: any) {
      console.error(error);
      Swal.fire({
        title: 'Error',
        text: error?.data?.message || 'Failed to update settings. Please try again.',
        icon: 'error',
        confirmButtonColor: '#0ea5e9',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)'
      });
    }
  };

  return {
    shark,
    setShark,
    direction,
    setDirection,
    currentIndex,
    currentShark,
    currentCostumeObj,
    navigateShark,
    handleSave,
    isSaving
  };
}
